import React, { useState } from 'react';
import {
  Settings as SettingsIcon,
  Check,
  ChevronRight,
  Bell,
  Lock,
  Link,
  BookOpen,
  Trophy,
  Accessibility,
  Globe,
  Moon,
  User,
  MessageSquare,
} from 'lucide-react';
import { UserStats, UserSettings } from '../types';

interface SettingsProps {
  userStats: UserStats;
  onUpdateAvatar: (index: number) => void;
  onUpdateSettings?: (settings: Partial<UserSettings>) => void;
}

const avatars = [
  'https://raw.githubusercontent.com/stackblitz/stackblitz-avatars/main/1.png',
  'https://raw.githubusercontent.com/stackblitz/stackblitz-avatars/main/2.png',
  'https://raw.githubusercontent.com/stackblitz/stackblitz-avatars/main/3.png',
  'https://raw.githubusercontent.com/stackblitz/stackblitz-avatars/main/4.png',
  'https://raw.githubusercontent.com/stackblitz/stackblitz-avatars/main/5.png',
  'https://raw.githubusercontent.com/stackblitz/stackblitz-avatars/main/6.png',
  'https://raw.githubusercontent.com/stackblitz/stackblitz-avatars/main/7.png',
  'https://raw.githubusercontent.com/stackblitz/stackblitz-avatars/main/8.png',
  'https://raw.githubusercontent.com/stackblitz/stackblitz-avatars/main/9.png',
  'https://raw.githubusercontent.com/stackblitz/stackblitz-avatars/main/10.png',
  'https://raw.githubusercontent.com/stackblitz/stackblitz-avatars/main/11.png',
  'https://raw.githubusercontent.com/stackblitz/stackblitz-avatars/main/12.png',
];

const Settings: React.FC<SettingsProps> = ({ userStats, onUpdateAvatar }) => {
  const [activeSection, setActiveSection] = useState<string>('avatar');

  const sections = [
    { id: 'avatar', label: 'Avatar & Appearance', icon: User },
    { id: 'chat', label: 'Chat & Conversation', icon: MessageSquare },
    { id: 'notifications', label: 'Notifications & Reminders', icon: Bell },
    { id: 'privacy', label: 'Privacy & Data Sharing', icon: Lock },
    { id: 'integrations', label: 'Integrations', icon: Link },
    { id: 'learning', label: 'Learning & Content', icon: BookOpen },
    { id: 'gamification', label: 'Gamification & Goals', icon: Trophy },
    { id: 'accessibility', label: 'Accessibility & UI', icon: Accessibility },
    { id: 'language', label: 'Language & Region', icon: Globe },
    { id: 'dnd', label: 'Do Not Disturb', icon: Moon },
  ];

  const renderSection = (sectionId: string) => {
    switch (sectionId) {
      case 'avatar':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">
              {avatars.map((avatar, index) => (
                <button
                  key={index}
                  onClick={() => onUpdateAvatar(index)}
                  className={`relative rounded-lg overflow-hidden aspect-square transition-transform hover:scale-105 ${
                    userStats.selectedAvatar === index ? 'ring-4 ring-blue-500' : ''
                  }`}
                >
                  <img
                    src={avatar}
                    alt={`Avatar ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  {userStats.selectedAvatar === index && (
                    <div className="absolute inset-0 bg-blue-500 bg-opacity-20 flex items-center justify-center">
                      <Check className="w-6 h-6 text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Enter your display name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pronouns
                </label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-md">
                  <option>They/Them</option>
                  <option>She/Her</option>
                  <option>He/Him</option>
                  <option>Custom</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Voice
                </label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-md">
                  <option>Friendly (Default)</option>
                  <option>Professional</option>
                  <option>Calm</option>
                  <option>Energetic</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 'chat':
        return (
          <div className="space-y-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Conversation Style
                </label>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span>Tone</span>
                    <select className="px-3 py-1 border border-gray-300 rounded-md">
                      <option>Casual</option>
                      <option>Professional</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span>Mood</span>
                    <select className="px-3 py-1 border border-gray-300 rounded-md">
                      <option>Cheerful</option>
                      <option>Neutral</option>
                      <option>Empathetic</option>
                      <option>Clinical</option>
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Session Length
                </label>
                <div className="flex gap-2">
                  <button className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md">
                    Short (1-2min)
                  </button>
                  <button className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-md">
                    Deep (5-10min)
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Memory & History
                </label>
                <div className="space-y-2">
                  <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span>Chat Memory</span>
                    <select className="px-3 py-1 border border-gray-300 rounded-md">
                      <option>Off</option>
                      <option>Session-only</option>
                      <option>Long-term</option>
                    </select>
                  </label>
                  <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span>Weekly Summary Email</span>
                    <div className="relative inline-block w-12 h-6 transition duration-200 ease-in-out">
                      <input type="checkbox" className="switch-checkbox" id="switch-new" />
                      <label
                        htmlFor="switch-new"
                        className="switch-label"
                      ></label>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="p-4 text-center text-gray-500">
            This section is under development
          </div>
        );
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg w-full h-full overflow-hidden flex">
      {/* Sidebar */}
      <div className="w-64 bg-gray-50 border-r border-gray-200 p-4">
        <div className="flex items-center mb-6">
          <SettingsIcon className="w-6 h-6 text-gray-600 mr-2" />
          <h2 className="text-xl font-bold text-gray-800">Settings</h2>
        </div>

        <nav className="space-y-1">
          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm ${
                  activeSection === section.id
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center">
                  <Icon className="w-5 h-5 mr-3" />
                  <span>{section.label}</span>
                </div>
                <ChevronRight className="w-4 h-4" />
              </button>
            );
          })}
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="max-w-2xl mx-auto">
          <h3 className="text-xl font-semibold text-gray-800 mb-6">
            {sections.find((s) => s.id === activeSection)?.label}
          </h3>
          {renderSection(activeSection)}
        </div>
      </div>
    </div>
  );
};

export default Settings;